import { useSyncExternalStore } from "react";

/**
 * The one thing the page has to say that the contract does not carry.
 *
 * Almost everything on screen is a view onto state the core worker broadcasts,
 * and that is the arrangement — a second source of truth is how two screens start
 * disagreeing. This is the exception, and the reason is structural rather than
 * convenient: **delivery happens in the page**, because the save dialog is a
 * window API, so what the page learns while delivering is not the worker's to
 * broadcast. What it learns is one sentence, and only on the path web ticket 01
 * measured as unreachable on every current Chromium: the browser could not record
 * MP4, so the take is the container it is, and nothing was converted.
 *
 * Deliberately not a piece of React state lifted somewhere. Delivery is attached
 * to the port once, at startup, outside the component tree — it has to be, or a
 * remount would answer one request twice and open two save dialogs — so what it
 * writes to has to outlive a render.
 */

let notice: string | undefined;
const listeners = new Set<() => void>();

/** Say something about the take that has just been delivered, or clear it. */
export function setTakeNotice(next: string | undefined): void {
  if (notice === next) return;
  notice = next;
  for (const listener of listeners) listener();
}

export function useTakeNotice(): string | undefined {
  return useSyncExternalStore(subscribe, () => notice);
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

/**
 * The saved take the user has closed the line about, if they have.
 *
 * `lastOutput` stays on every status until the next take starts, which is right
 * for the status — it is what the desktop's "show in folder" reads — and wrong
 * for a line on screen, which then sat over the prompter for as long as nobody
 * recorded again. Closing it is a fact about this page and not about the take,
 * so it lives here rather than in the worker.
 *
 * Module-level for the reason the notice is: the line is drawn in the prompter's
 * chrome, which unmounts on the way to the room, and a closed line that came back
 * on the way in would be a close that did not take.
 */
let dismissed: string | undefined;
const dismissListeners = new Set<() => void>();

/** Close the line about this saved take, or forget a close with `undefined`. */
export function dismissSavedTake(output: string | undefined): void {
  if (dismissed === output) return;
  dismissed = output;
  for (const listener of dismissListeners) listener();
}

export function useDismissedTake(): string | undefined {
  return useSyncExternalStore(subscribeDismissed, () => dismissed);
}

function subscribeDismissed(listener: () => void): () => void {
  dismissListeners.add(listener);
  return () => dismissListeners.delete(listener);
}
